import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useNavigation } from '../shared/hooks/useNavigation'
import { ROUTES } from '../shared/navigation'
import { 
  ArrowLeft, 
  Edit, 
  Phone, 
  Mail, 
  Calendar, 
  User, 
  Scissors, 
  Clock, 
  MapPin,
  Trash2,
  Loader2,
  AlertTriangle,
  Tag,
  CreditCard
} from '../components/icons'
import { clientService } from '../services/clientService'
import { useAuthStore } from '../stores/authStore'
import LabelChip from '../components/labels/LabelChip'
import { formatCpf, formatAddressForDisplay } from '../utils/brazilianUtils'

const ClientDetailPage = () => {
  const { clientId } = useParams()
  const { navigate } = useNavigation()
  const { hasRole } = useAuthStore()
  
  // State
  const [client, setClient] = useState(null)
  const [appointments, setAppointments] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingAppointments, setIsLoadingAppointments] = useState(false)
  const [error, setError] = useState('')
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showAllAppointments, setShowAllAppointments] = useState(false)

  // Permissions
  const canEditClient = hasRole(['GESTOR', 'ATENDENTE'])
  const canDeleteClient = hasRole(['GESTOR'])

  // Load client data
  useEffect(() => {
    const loadClientData = async () => {
      try {
        setIsLoading(true)
        setError('')
        
        const clientData = await clientService.getClientById(clientId)
        if (!clientData) {
          setError('Cliente não encontrado')
          return
        }
        
        setClient(clientData)
        
        // Load appointments
        setIsLoadingAppointments(true)
        const appointmentsData = await clientService.getClientAppointments(clientId)
        setAppointments(appointmentsData)
        
      } catch (error) {
        setError('Erro ao carregar dados do cliente')
      } finally {
        setIsLoading(false)
        setIsLoadingAppointments(false)
      }
    }

    if (clientId) {
      loadClientData()
    }
  }, [clientId])

  // Handle delete client
  const handleDeleteClient = async () => {
    try {
      setIsDeleting(true)
      const success = await clientService.deleteClient(clientId)
      
      if (success) {
        navigate(ROUTES.PROFESSIONAL.CLIENTS, { 
          state: { message: 'Cliente excluído com sucesso' }
        })
      } else {
        setError('Erro ao excluir cliente')
      }
    } catch (error) {
      setError('Erro ao excluir cliente')
    } finally {
      setIsDeleting(false)
      setShowDeleteModal(false)
    }
  }

  // Format utilities
  const formatPhone = (phone) => {
    if (!phone) return ''
    const cleaned = phone.replace(/\D/g, '')
    if (cleaned.length === 11) {
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`
    }
    return phone
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Data não informada'
    
    try {
      // Handle different date formats
      const date = new Date(dateString)
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Data inválida'
      }
      
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch (error) {
      return 'Data inválida'
    }
  }

  const formatAppointmentDateTime = (appointment) => {
    if (!appointment.appointment_date) return 'Data não informada'
    
    try {
      // Combine date and time from the appointment object
      const dateStr = appointment.appointment_date // "2025-07-02"
      const timeStr = appointment.start_time || '00:00:00' // "09:30:00"
      
      // Create a proper datetime string
      const dateTimeStr = `${dateStr}T${timeStr}`
      const date = new Date(dateTimeStr)
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return `${dateStr} ${timeStr.slice(0, 5)}`
      }
      
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch (error) {
      return 'Data inválida'
    }
  }

  const getInitials = (name) => {
    if (!name) return '?'
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }

  const getGenderLabel = (gender) => {
    const labels = {
      'MASCULINO': 'Masculino',
      'FEMININO': 'Feminino',
      'OUTROS': 'Outros'
    }
    return labels[gender] || gender
  }


  const getAppointmentStatusColor = (status) => {
    const normalizedStatus = status?.toLowerCase()
    const colors = {
      'scheduled': 'bg-transparent border border-blue-600/30 dark:border-blue-400/30 text-blue-600 dark:text-blue-400',
      'confirmed': 'bg-transparent border border-green-600/30 dark:border-green-400/30 text-green-600 dark:text-green-400',
      'completed': 'bg-transparent border border-brand-dark/30 dark:border-brand-light/30 text-brand-dark dark:text-brand-light',
      'cancelled': 'bg-transparent border border-red-600/30 dark:border-red-400/30 text-red-600 dark:text-red-400',
      'no_show': 'bg-transparent border border-amber-600/30 dark:border-amber-400/30 text-amber-600 dark:text-amber-400'
    }
    return colors[normalizedStatus] || 'bg-transparent border border-brand-dark/30 dark:border-brand-light/30 text-brand-dark dark:text-brand-light'
  }

  const getAppointmentStatusLabel = (status) => {
    const normalizedStatus = status?.toLowerCase()
    const labels = {
      'scheduled': 'Agendado',
      'confirmed': 'Confirmado',
      'completed': 'Concluído',
      'cancelled': 'Cancelado',
      'no_show': 'Não compareceu'
    }
    return labels[normalizedStatus] || status
  }

  // Loading state - Luxury Design
  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-brand-light dark:bg-brand-dark">
        <div className="w-8 h-8 border-2 border-brand-dark dark:border-brand-light border-t-transparent animate-spin"></div>
      </div>
    )
  }

  // Error state - Luxury Design
  if (error && !client) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-brand-light dark:bg-brand-dark px-6">
        <div className="w-16 h-16 bg-transparent border border-red-600/30 dark:border-red-400/30 flex items-center justify-center mb-4">
          <AlertTriangle size={32} className="text-red-600 dark:text-red-400" strokeWidth={1.5} />
        </div>
        <h2 className="text-xl font-serif font-bold text-brand-dark dark:text-brand-light mb-2 tracking-wide">Erro</h2>
        <p className="text-brand-dark/60 dark:text-brand-light/60 text-center mb-6 tracking-wide">{error}</p>
        <button
          onClick={() => navigate(ROUTES.PROFESSIONAL.CLIENTS)}
          className="bg-brand-dark dark:bg-brand-light text-brand-light dark:text-brand-dark border border-brand-dark dark:border-brand-light px-6 py-3 font-medium uppercase tracking-widest text-sm hover:bg-brand-dark/90 dark:hover:bg-brand-light/90 transition-all duration-300"
        >
          Voltar para Clientes
        </button>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-brand-light dark:bg-brand-dark">
      {/* Header - Luxury Design */}
      <div className="bg-brand-light dark:bg-brand-dark border-b border-brand-dark/10 dark:border-brand-light/10 px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={() => navigate(ROUTES.PROFESSIONAL.CLIENTS)}
              className="mr-3 p-2 hover:bg-brand-dark/5 dark:hover:bg-brand-light/5 border border-brand-dark/20 dark:border-brand-light/20 transition-all duration-300"
            >
              <ArrowLeft size={20} className="text-brand-dark dark:text-brand-light" strokeWidth={1.5} />
            </button>
            <div>
              <h1 className="text-xl font-serif font-bold text-brand-dark dark:text-brand-light tracking-wide">Detalhes do Cliente</h1>
              <p className="text-brand-dark/60 dark:text-brand-light/60 text-sm tracking-wide">Informações e histórico</p>
            </div>
          </div>

          {canEditClient && (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => navigate(ROUTES.PROFESSIONAL.CLIENT_EDIT(clientId))}
                className="p-2 bg-transparent border border-brand-dark/20 dark:border-brand-light/20 hover:bg-brand-dark/5 dark:hover:bg-brand-light/5 transition-all duration-300"
              >
                <Edit size={18} className="text-brand-dark dark:text-brand-light" strokeWidth={1.5} />
              </button>

              {canDeleteClient && (
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="p-2 bg-transparent border border-red-600/30 dark:border-red-400/30 hover:bg-red-600/5 dark:hover:bg-red-400/5 transition-all duration-300"
                >
                  <Trash2 size={18} className="text-red-600 dark:text-red-400" strokeWidth={1.5} />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto pb-6 mobile-scroll">
        {/* Client Profile Card - Luxury Design */}
        <div className="bg-brand-light dark:bg-brand-dark mx-4 mt-4 border border-brand-dark/20 dark:border-brand-light/20 p-6">
          <div className="flex items-center mb-6">
            <div className="w-16 h-16 bg-brand-dark/10 dark:bg-brand-light/10 border border-brand-dark/20 dark:border-brand-light/20 flex items-center justify-center mr-4">
              <span className="text-brand-dark dark:text-brand-light font-bold text-lg tracking-wide">
                {getInitials(client?.full_name)}
              </span>
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-serif font-bold text-brand-dark dark:text-brand-light tracking-wide">
                {client?.full_name || 'Nome não informado'}
              </h2>
              <div className="flex items-center mt-1">
                <div className={`w-2 h-2 mr-2 ${client?.is_active ? 'bg-green-600 dark:bg-green-400' : 'bg-brand-dark/40 dark:bg-brand-light/40'}`} />
                <span className="text-sm text-brand-dark/60 dark:text-brand-light/60 tracking-wide">
                  {client?.is_active ? 'Cliente ativo' : 'Cliente inativo'}
                </span>
              </div>
            </div>
          </div>

          {/* Contact Information - Luxury Design */}
          <div className="space-y-4">
            <div className="flex items-center">
              <Mail size={20} className="text-brand-dark/40 dark:text-brand-light/40 mr-3" strokeWidth={1.5} />
              <div>
                <p className="text-sm text-brand-dark/60 dark:text-brand-light/60 tracking-wide">Email</p>
                <p className="font-medium text-brand-dark dark:text-brand-light tracking-wide">{client?.email}</p>
              </div>
            </div>

            {client?.phone_number && (
              <div className="flex items-center">
                <Phone size={20} className="text-brand-dark/40 dark:text-brand-light/40 mr-3" strokeWidth={1.5} />
                <div>
                  <p className="text-sm text-brand-dark/60 dark:text-brand-light/60 tracking-wide">Telefone</p>
                  <p className="font-medium text-brand-dark dark:text-brand-light tracking-wide">{formatPhone(client.phone_number)}</p>
                </div>
              </div>
            )}

            {client?.date_of_birth && (
              <div className="flex items-center">
                <Calendar size={20} className="text-brand-dark/40 dark:text-brand-light/40 mr-3" strokeWidth={1.5} />
                <div>
                  <p className="text-sm text-brand-dark/60 dark:text-brand-light/60 tracking-wide">Data de Nascimento</p>
                  <p className="font-medium text-brand-dark dark:text-brand-light tracking-wide">{formatDate(client.date_of_birth)}</p>
                </div>
              </div>
            )}

            {client?.gender && (
              <div className="flex items-center">
                <User size={20} className="text-brand-dark/40 dark:text-brand-light/40 mr-3" strokeWidth={1.5} />
                <div>
                  <p className="text-sm text-brand-dark/60 dark:text-brand-light/60 tracking-wide">Gênero</p>
                  <p className="font-medium text-brand-dark dark:text-brand-light tracking-wide">{getGenderLabel(client.gender)}</p>
                </div>
              </div>
            )}

            {client?.cpf && (
              <div className="flex items-center">
                <CreditCard size={20} className="text-brand-dark/40 dark:text-brand-light/40 mr-3" strokeWidth={1.5} />
                <div>
                  <p className="text-sm text-brand-dark/60 dark:text-brand-light/60 tracking-wide">CPF</p>
                  <p className="font-medium text-brand-dark dark:text-brand-light tracking-wide">{formatCpf(client.cpf)}</p>
                </div>
              </div>
            )}

            {(client?.address_street || client?.address_city || client?.address_cep) && (
              <div className="flex items-start">
                <MapPin size={20} className="text-brand-dark/40 dark:text-brand-light/40 mr-3 mt-0.5" strokeWidth={1.5} />
                <div>
                  <p className="text-sm text-brand-dark/60 dark:text-brand-light/60 tracking-wide">Endereço</p>
                  <p className="font-medium text-brand-dark dark:text-brand-light whitespace-pre-line tracking-wide">{formatAddressForDisplay(client)}</p>
                </div>
              </div>
            )}

            {/* Labels Section */}
            {client?.labels && client.labels.length > 0 && (
              <div className="flex items-start mt-4 pt-4 border-t border-brand-dark/10 dark:border-brand-light/10">
                <Tag size={20} className="text-brand-dark/40 dark:text-brand-light/40 mr-3 mt-0.5" strokeWidth={1.5} />
                <div className="flex-1">
                  <p className="text-sm text-brand-dark/60 dark:text-brand-light/60 mb-3 tracking-wide">Preferências</p>
                  <div className="flex flex-wrap gap-2">
                    {client.labels.map((label) => (
                      <LabelChip
                        key={label.id}
                        label={label}
                        size="medium"
                        showRemove={false}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>

        {/* Appointments History - Luxury Design */}
        <div className="bg-brand-light dark:bg-brand-dark mx-4 mt-4 border border-brand-dark/20 dark:border-brand-light/20">
          <div className="p-6 border-b border-brand-dark/10 dark:border-brand-light/10">
            <h3 className="text-lg font-serif font-bold text-brand-dark dark:text-brand-light tracking-wide">Histórico de Agendamentos</h3>
            <p className="text-brand-dark/60 dark:text-brand-light/60 text-sm tracking-wide">
              {appointments.length > 0 ? `${appointments.length} agendamentos` : 'Nenhum agendamento'}
            </p>
          </div>

          {isLoadingAppointments ? (
            <div className="p-6 flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-brand-dark dark:border-brand-light border-t-transparent animate-spin"></div>
            </div>
          ) : appointments.length === 0 ? (
            <div className="p-6 text-center">
              <div className="w-12 h-12 bg-transparent border border-brand-dark/20 dark:border-brand-light/20 flex items-center justify-center mx-auto mb-3">
                <Calendar size={24} className="text-brand-dark/40 dark:text-brand-light/40" strokeWidth={1.5} />
              </div>
              <p className="text-brand-dark/60 dark:text-brand-light/60 tracking-wide">Nenhum agendamento encontrado</p>
            </div>
          ) : (
            <div className="divide-y divide-brand-dark/10 dark:divide-brand-light/10">
              {(showAllAppointments ? appointments : appointments.slice(0, 5)).map((appointment) => (
                <div key={appointment.id} className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <Clock size={16} className="text-brand-dark/40 dark:text-brand-light/40 mr-2" strokeWidth={1.5} />
                        <span className="text-sm font-medium text-brand-dark dark:text-brand-light tracking-wide">
                          {formatAppointmentDateTime(appointment)}
                        </span>
                        <span className={`ml-2 px-2 py-1 text-xs uppercase tracking-wider ${getAppointmentStatusColor(appointment.status)}`}>
                          {getAppointmentStatusLabel(appointment.status)}
                        </span>
                      </div>

                      {appointment.service && appointment.service.name ? (
                        <div className="text-sm text-brand-dark/60 dark:text-brand-light/60 mb-1 tracking-wide">
                          <p><strong>Serviço:</strong> {appointment.service.name}</p>
                        </div>
                      ) : appointment.services && appointment.services.length > 0 ? (
                        <div className="text-sm text-brand-dark/60 dark:text-brand-light/60 mb-1 tracking-wide">
                          <p><strong>Serviços:</strong> {appointment.services.map(s => s.name).join(', ')}</p>
                        </div>
                      ) : (
                        <div className="text-sm text-brand-dark/50 dark:text-brand-light/50 mb-1 tracking-wide">
                          <p><em>Serviço não especificado</em></p>
                        </div>
                      )}

                      {(appointment.professional?.full_name || appointment.professional_name) && (
                        <div className="flex items-center mt-1 text-sm text-brand-dark/50 dark:text-brand-light/50 tracking-wide">
                          <User size={14} className="mr-1" strokeWidth={1.5} />
                          <span>{appointment.professional?.full_name || appointment.professional_name}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {appointments.length > 5 && (
                <div className="p-4 text-center">
                  <button
                    onClick={() => setShowAllAppointments(!showAllAppointments)}
                    className="text-brand-dark dark:text-brand-light text-sm font-medium uppercase tracking-widest hover:opacity-70 transition-opacity"
                  >
                    {showAllAppointments
                      ? 'Mostrar menos'
                      : `Ver todos os agendamentos (${appointments.length})`
                    }
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal - Luxury Design */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center p-4 z-50">
          <div className="bg-brand-light dark:bg-brand-dark border border-brand-dark/20 dark:border-brand-light/20 p-6 max-w-sm w-full">
            <div className="text-center">
              <div className="w-12 h-12 bg-transparent border border-red-600/30 dark:border-red-400/30 flex items-center justify-center mx-auto mb-4">
                <AlertTriangle size={24} className="text-red-600 dark:text-red-400" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-serif font-bold text-brand-dark dark:text-brand-light mb-2 tracking-wide">Excluir Cliente</h3>
              <p className="text-brand-dark/60 dark:text-brand-light/60 mb-6 tracking-wide">
                Tem certeza que deseja excluir este cliente? Esta ação não pode ser desfeita.
              </p>

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  disabled={isDeleting}
                  className="flex-1 py-3 px-4 bg-transparent border border-brand-dark/20 dark:border-brand-light/20 text-brand-dark dark:text-brand-light font-medium uppercase tracking-widest text-sm disabled:opacity-50 hover:bg-brand-dark/5 dark:hover:bg-brand-light/5 transition-all duration-300"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeleteClient}
                  disabled={isDeleting}
                  className="flex-1 py-3 px-4 bg-red-600 dark:bg-red-400 text-white border border-red-600 dark:border-red-400 font-medium uppercase tracking-widest text-sm disabled:opacity-50 flex items-center justify-center hover:bg-red-700 dark:hover:bg-red-500 transition-all duration-300"
                >
                  {isDeleting ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent animate-spin"></div>
                  ) : (
                    'Excluir'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ClientDetailPage